// The Space "Website tile" — its data contract and the copy decisions, kept pure so they are testable
// without rendering it. The tile itself was replaced by the Space header's presence line and its Manage dialog
// (components/symphony/website/SpacePresence.tsx, rules in lib/presence.ts), which read these.
//
// The shape is symphony-bff's `WorkspaceWebsite` (on ListWorkspaces' WorkspaceSummary and ListProjects'
// Project), passed through /api/workspaces unchanged. orion-web's CLIENT bundle must not import
// common-lib, so it is restated here as a client-local contract, like `WorkspaceSummary` in
// lib/workspaces.ts. Every field is optional on the wire (proto3), so every reader defaults it.

/** A Space's primary Wix site, as the website tile shows it. */
export interface WorkspaceWebsite {
  metaSiteId?: string;
  name?: string;
  /** The address the site answers at today. */
  url?: string;
  /** The home page's picture from the last site check; empty until it has one. */
  screenshotUrl?: string;
  /** Whether the site has a paid Wix plan. */
  premium?: boolean;
  /** The site's own domain; empty on Wix's free address. */
  customDomain?: string;
  /** How many other sites the Space holds besides this one. */
  otherSites?: number;
  /**
   * Whether the site is published (proto BoolValue → a plain boolean, or absent). `false` is a site that is
   * not live yet, which the tile offers to put live; ABSENT is unknown (an older BFF, a failed read) and
   * reads as live.
   */
  published?: boolean | null;
}

/**
 * The site the tile should show, or null (the same rule as both phones). A website with an empty `url`
 * counts as none — there is no address to link or edit — UNLESS it is a known not-live site
 * (`published === false`) with a metasite id: an unpublished site has no address yet, and that is exactly
 * the site the tile offers to put live.
 */
export function presentWebsite(website: WorkspaceWebsite | null | undefined): WorkspaceWebsite | null {
  if (!website) return null;
  if ((website.url ?? '').trim()) return website;
  return websiteNotLive(website) && (website.metaSiteId ?? '').trim() ? website : null;
}

/** Whether the site is known NOT to be live yet. Only an explicit `false` counts: absent is unknown, read as live. */
export function websiteNotLive(website: WorkspaceWebsite): boolean {
  return website.published === false;
}

/** An i18n key with the `{name}` values it interpolates. */
export interface WebsiteCopy {
  key: string;
  values?: Record<string, string | number>;
}

/** The address's host for display ("www.example.com"), or the address as given when it does not parse. */
export function websiteHost(url: string | undefined): string {
  const raw = (url ?? '').trim();
  if (!raw) return '';
  try {
    return new URL(raw).host;
  } catch {
    return raw;
  }
}

/** The seeded message for "Edit with chat". */
export function websiteEditPrompt(website: WorkspaceWebsite): WebsiteCopy {
  // A site not live yet may have no address: it is named instead.
  return { key: 'workspace.website.editPrompt', values: { url: (website.url ?? '').trim() || websiteDisplayName(website) } };
}

/** The site's display name: its name, else the address's host, else empty (trimmed). */
export function websiteDisplayName(website: WorkspaceWebsite): string {
  return (website.name ?? '').trim() || websiteHost(website.url);
}

/** The seeded message for "Go live" on a site that is not live yet, naming the site (name, else host, else empty). */
export function websitePublishPrompt(website: WorkspaceWebsite): WebsiteCopy {
  return { key: 'workspace.website.publishPrompt', values: { name: websiteDisplayName(website) } };
}

/** Where "Manage plan" goes: the owner's Wix subscriptions page. */
export const MANAGE_PLAN_URL = 'https://manage.wix.com/account/subscriptions';
/** Where "Manage domain" goes: the owner's Wix domains page. */
export const MANAGE_DOMAIN_URL = 'https://manage.wix.com/account/domains';

/** What a strip cell's action does: start a new chat seeded with a prompt, or open a Wix page in a new tab. */
export type WebsiteStripAction = { kind: 'chat'; prompt: WebsiteCopy } | { kind: 'link'; url: string };

/** One cell of the strip: a label over a text action. */
export interface WebsiteStripCell<Label> {
  label: Label;
  actionKey: string;
  action: WebsiteStripAction;
}

/** The plan | domain strip under the tile's address, modelled on the Wix dashboard home's header. */
export interface WebsiteStrip {
  /** The label is an i18n key ("Free plan" / "Premium plan"). */
  plan: WebsiteStripCell<string>;
  /** The label is display text: the custom domain, or the site's host. */
  domain: WebsiteStripCell<string>;
}

/**
 * The tile's plan | domain strip (the same rule as both phones):
 *   - plan: a custom domain OR `premium` → "Premium plan" / "Manage plan" (the Wix subscriptions page) —
 *     a custom domain requires a paid plan, so it says Premium whatever `premium` reads; otherwise
 *     "Free plan" / "Upgrade" (a new chat seeded with `upgradePrompt`);
 *   - domain: a custom domain → the domain / "Manage domain" (the Wix domains page); otherwise the
 *     site's host / "Get your own domain" (a new chat seeded with `goLivePrompt`).
 * The free-site actions go through chat, not a checkout link: the agent first checks what the owner
 * already has (a plan on another site it can move, a domain already on Wix), where a direct checkout
 * would sell a second plan to someone who already pays for one.
 * A blank (whitespace-only) custom domain counts as none.
 */
export function websiteStrip(website: WorkspaceWebsite): WebsiteStrip {
  const url = (website.url ?? '').trim();
  const domain = (website.customDomain ?? '').trim();
  const paid = !!domain || website.premium === true;
  return {
    plan: paid
      ? { label: 'workspace.website.planPremium', actionKey: 'workspace.website.managePlan', action: { kind: 'link', url: MANAGE_PLAN_URL } }
      : {
          label: 'workspace.website.planFree',
          actionKey: 'workspace.website.upgrade',
          action: { kind: 'chat', prompt: { key: 'workspace.website.upgradePrompt', values: { url } } },
        },
    domain: domain
      ? { label: domain, actionKey: 'workspace.website.manageDomain', action: { kind: 'link', url: MANAGE_DOMAIN_URL } }
      : {
          label: websiteHost(url),
          actionKey: 'workspace.website.getDomain',
          action: { kind: 'chat', prompt: { key: 'workspace.website.goLivePrompt', values: { url } } },
        },
  };
}

/** The "+{count} other versions" note, or null when the Space holds no other site. */
export function websiteOtherVersions(website: WorkspaceWebsite): WebsiteCopy | null {
  const count = Math.max(0, Math.floor(Number(website.otherSites) || 0));
  return count > 0 ? { key: 'workspace.website.otherVersions', values: { count } } : null;
}

/**
 * The HIDDEN context the tile's chat handoffs carry, naming the Space and the site by id, so the agent
 * works on THIS Space's site without asking which (the visible seed names only the address). Rides
 * `ChatHandoffNew.context`, the same channel `createGoalContext` (lib/goals.ts) uses. Undefined when
 * there is no workspace id to scope to.
 */
export function websiteChatContext(workspaceId: string | undefined, workspaceName: string, website: WorkspaceWebsite): string | undefined {
  if (!workspaceId) return undefined;
  const site = website.metaSiteId ? ` (metasite id "${website.metaSiteId}")` : '';
  return (
    `The user is asking about the website of their "${workspaceName}" Space (workspace id "${workspaceId}"): ` +
    `"${website.name ?? ''}" at ${(website.url ?? '').trim()}${site}. Work in this Space, on this site. Don't ask which site.`
  );
}
